import { useState } from 'react';
import { type Maybe } from './maybe';
import { getLabelText } from './plain-label.utils';

const CREATE_OPTION_MARKER = '__guardianMultiSelectCreateOption';

function isCreateOptionValue(val: unknown, optionValue?: string): boolean {
    if (optionValue) {
        return val === CREATE_OPTION_MARKER;
    }

    return (
        Boolean(val) &&
        typeof val === 'object' &&
        (val as Record<string, unknown>)[CREATE_OPTION_MARKER] === true
    );
}

function buildCreateOption(
    label: string,
    optionLabel: Maybe<string>,
    optionValue: Maybe<string>,
): Record<string, unknown> {
    const option: Record<string, unknown> = {
        [CREATE_OPTION_MARKER]: true,
    };

    if (optionLabel) {
        option[optionLabel] = label;
    }

    if (optionValue) {
        option[optionValue] = CREATE_OPTION_MARKER;
    }

    return option;
}

export interface UseCreatableMultiSelectInput {
    creatable?: boolean;
    options?: Maybe<unknown[]>;
    optionLabel?: string;
    optionValue?: string;
    createOptionLabel?: (query: string) => string;
}

export interface MultiSelectFilterEventInput {
    filter: string;
}

export interface CreateSelectionResult {
    createdValue: Maybe<string>;
    nextValue: unknown;
}

export interface UseCreatableMultiSelectResult {
    options: Maybe<unknown[]>;
    filter: boolean | undefined;
    handleFilter: (event: MultiSelectFilterEventInput) => void;
    extractCreateSelection: (value: unknown) => CreateSelectionResult;
}

const defaultCreateOptionLabel = (query: string): string => `Add "${query}"`;

export function useCreatableMultiSelect({
    creatable,
    options,
    optionLabel,
    optionValue,
    createOptionLabel = defaultCreateOptionLabel,
}: UseCreatableMultiSelectInput): UseCreatableMultiSelectResult {
    const [query, setQuery] = useState('');

    const handleFilter = (event: MultiSelectFilterEventInput): void => {
        setQuery(event.filter);
    };

    if (!creatable) {
        return {
            options,
            filter: undefined,
            handleFilter,
            extractCreateSelection: (value) => ({
                createdValue: null,
                nextValue: value,
            }),
        };
    }

    const trimmedQuery = query.trim();
    const normalizedQuery = trimmedQuery.toLowerCase();
    const hasMatch =
        trimmedQuery.length > 0 &&
        Array.isArray(options) &&
        options.some(
            (option) =>
                getLabelText(option, optionLabel)?.trim().toLowerCase() ===
                normalizedQuery,
        );
    const canCreate = trimmedQuery.length > 0 && !hasMatch;

    const optionsWithCreate = canCreate
        ? [
              ...(options ?? []),
              buildCreateOption(
                  createOptionLabel(trimmedQuery),
                  optionLabel,
                  optionValue,
              ),
          ]
        : options;

    const extractCreateSelection = (value: unknown): CreateSelectionResult => {
        if (!Array.isArray(value)) {
            return { createdValue: null, nextValue: value };
        }

        const createIndex = value.findIndex((entry) =>
            isCreateOptionValue(entry, optionValue),
        );

        if (createIndex === -1) {
            return { createdValue: null, nextValue: value };
        }

        const nextValue = value.filter((_, index) => index !== createIndex);

        return { createdValue: trimmedQuery, nextValue };
    };

    return {
        options: optionsWithCreate,
        filter: true,
        handleFilter,
        extractCreateSelection,
    };
}
